import React, { useEffect, useState } from "react";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton } from "@/hq/HQPrimitives";
import { FixJobDrawer } from "@/hq/HQOptimiseFix";
import OrangeFixProofCard from "@/shared/OrangeFixProofCard";
import { toast } from "sonner";

// Phase 3E-A — Orange Fix area on the Client Profile.
// Two panels:
//   1. Detected issues (OPEN + REGRESSED) — inline "Request Fix" per row
//   2. Fix jobs for this client — REQUESTED / REVIEWING / CANCELLED
//      with drawer detail (remediation, access checklist, cancel)

const ISSUE_TONE = {
  OPEN:      { bg: "#FFF7EE", fg: "#C1350B", bd: "#FCE1C7" },
  REGRESSED: { bg: "#FFE9DA", fg: "#C1350B", bd: "#FCE1C7" },
  RESOLVED:  { bg: "#EBFBF2", fg: "#0FA958", bd: "#B7EBCF" },
};
const JOB_TONE = {
  REQUESTED:  { bg: "#FFF7EE", fg: "#C1350B", bd: "#FCE1C7" },
  REVIEWING:  { bg: "#F0F7FF", fg: "#2E7CF6", bd: "#D6E7FF" },
  CANCELLED:  { bg: "#F2F2F2", fg: "#4A4A4A", bd: "#EAEAEA" },
};

function Pill({ value, tone }) {
  const c = tone[value] || { bg: "#F2F2F2", fg: "#4A4A4A", bd: "#EAEAEA" };
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.16em] uppercase"
          style={{ background: c.bg, color: c.fg, border: `1px solid ${c.bd}` }}>
      {value || "—"}
    </span>
  );
}

export default function ClientOrangeFixTab({ businessId }) {
  const [issues, setIssues] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [busyIssue, setBusyIssue] = useState(null);
  const [selected, setSelected] = useState(null);
  const [proofFor, setProofFor] = useState(null);
  const [proof, setProof] = useState(null);

  const loadIssues = () =>
    hqApi.get(`/control/clients/${businessId}/issues`)
      .then((r) => setIssues(r.data))
      .catch(() => setIssues({ summary: {}, items: [] }));

  const loadJobs = () =>
    hqApi.get(`/control/clients/${businessId}/fix-requests`)
      .then((r) => setJobs(r.data))
      .catch(() => setJobs({ items: [] }));

  useEffect(() => { loadIssues(); loadJobs(); /* eslint-disable-next-line */ }, [businessId]);

  const requestFix = async (issueId) => {
    setBusyIssue(issueId);
    try {
      const r = await hqApi.post(`/control/clients/${businessId}/fix-requests`,
        { issue_ledger_id: issueId });
      if (r.data?._reused) toast.info("A fix job is already open for this issue.");
      else toast.success("Fix requested");
      await loadJobs();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusyIssue(null); }
  };

  const openProof = async (job) => {
    setProofFor(job.id); setProof(null);
    try {
      const r = await hqApi.get(`/control/fix-requests/${job.id}/proof`);
      setProof(r.data);
    } catch { setProof({ available: false }); }
  };

  const activeIssueIds = new Set(
    (jobs?.items || [])
      .filter((j) => !["CANCELLED", "VERIFIED_RESOLVED", "STILL_DETECTED"].includes(j.status))
      .map((j) => j.issue_ledger_id)
  );

  return (
    <div className="space-y-5" data-testid="client-orange-fix-tab">
      <Card className="p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Detected issues open now</div>
            <p className="mt-1 text-[13px] text-[#4A4A4A] max-w-lg">
              Sourced from the canonical Phase 3D issue ledger. Requesting a fix creates a job you can quote and complete later.
            </p>
          </div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#8A8A8A]">
            {issues ? `${(issues.summary?.open_count || 0) + (issues.summary?.regressed_count || 0)} open / regressed` : "…"}
          </div>
        </div>

        <div className="mt-4">
          {!issues ? (
            <div className="space-y-2">{[0,1,2].map(i => <Skeleton key={i} h={40}/>)}</div>
          ) : (issues.items || []).filter((i) => ["OPEN", "REGRESSED"].includes(i.status)).length === 0 ? (
            <EmptyState title="No open issues" hint="Every finding on the last scan has been resolved."
                        testId="client-orange-fix-issues-empty"/>
          ) : (
            <div className="divide-y divide-[#EAEAEA]" data-testid="client-orange-fix-issue-list">
              {(issues.items || [])
                .filter((i) => ["OPEN", "REGRESSED"].includes(i.status))
                .map((i) => {
                  const already = activeIssueIds.has(i.id);
                  return (
                    <div key={i.id} className="py-3 flex items-start gap-3 flex-wrap md:flex-nowrap"
                         data-testid={`client-orange-fix-issue-${i.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Pill value={i.status} tone={ISSUE_TONE}/>
                          <span className="text-[10px] tracking-[0.14em] uppercase text-[#8A8A8A] font-bold">
                            {i.category || "—"}{i.code ? ` · ${i.code}` : ""}
                          </span>
                        </div>
                        <div className="mt-1 font-bold text-[14px] text-[#0A0A0A] break-words">
                          {i.title || i.issue_key || "—"}
                        </div>
                        <div className="mt-0.5 text-[11px] text-[#8A8A8A]">
                          last seen {i.last_seen_at ? new Date(i.last_seen_at).toLocaleString() : "—"}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => requestFix(i.id)}
                        disabled={busyIssue === i.id || already}
                        data-testid={`client-orange-fix-request-${i.id}`}
                        className="px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.18em] uppercase transition-colors disabled:opacity-50 bg-[#FF6A00] text-black hover:bg-[#e75f00]"
                      >
                        {already ? "Fix pending" : busyIssue === i.id ? "Requesting…" : "Request fix"}
                      </button>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Fix jobs for this client</div>
            <p className="mt-1 text-[13px] text-[#4A4A4A] max-w-lg">
              Every fix job — active or historical — created against this client's issues.
            </p>
          </div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#8A8A8A]">
            {jobs ? `${jobs.total || (jobs.items?.length ?? 0)} total` : "…"}
          </div>
        </div>

        <div className="mt-4">
          {!jobs ? (
            <div className="space-y-2">{[0,1].map(i => <Skeleton key={i} h={40}/>)}</div>
          ) : (jobs.items || []).length === 0 ? (
            <EmptyState title="No fix jobs yet" hint="Request a fix from any open issue above."
                        testId="client-orange-fix-jobs-empty"/>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[14px]" data-testid="client-orange-fix-job-table">
                <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A]">
                  <tr>
                    <th className="text-left px-4 py-2 font-bold">Issue</th>
                    <th className="text-left px-4 py-2 font-bold">Status</th>
                    <th className="text-left px-4 py-2 font-bold">Requested</th>
                    <th className="text-left px-4 py-2 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.items.map((j) => (
                    <tr key={j.id} className="border-t border-[#EAEAEA]"
                        data-testid={`client-orange-fix-job-${j.id}`}>
                      <td className="px-4 py-2 font-bold break-words max-w-md">
                        <div>{j.title_at_request || j.issue_key || "—"}</div>
                        <div className="mt-0.5 text-[10px] tracking-[0.14em] uppercase text-[#8A8A8A]">
                          {j.category || "—"}{j.code ? ` · ${j.code}` : ""}
                        </div>
                      </td>
                      <td className="px-4 py-2"><Pill value={j.status} tone={JOB_TONE}/></td>
                      <td className="px-4 py-2 text-[12px] text-[#8A8A8A]">
                        {j.requested_at ? new Date(j.requested_at).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setSelected(j)}
                            data-testid={`client-orange-fix-job-view-${j.id}`}
                            className="px-3 py-1.5 rounded-full border border-[#EAEAEA] bg-white text-[10px] font-black tracking-[0.2em] uppercase hover:border-[#FF6A00]"
                          >
                            View
                          </button>
                          {j.status === "VERIFIED_RESOLVED" && (
                            <button
                              type="button"
                              onClick={() => openProof(j)}
                              data-testid={`client-orange-fix-job-result-${j.id}`}
                              className="px-3 py-1.5 rounded-full bg-[#0FA958] text-white text-[10px] font-black tracking-[0.2em] uppercase hover:bg-[#0d924b]"
                            >
                              View result ✓
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {selected && (
        <FixJobDrawer
          job={selected}
          onClose={() => setSelected(null)}
          onChanged={() => { loadJobs(); setSelected(null); }}
        />
      )}

      {proofFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 bg-black/40 overflow-y-auto"
             data-testid="client-orange-fix-proof-modal"
             onClick={(e) => { if (e.target === e.currentTarget) { setProofFor(null); setProof(null); }}}>
          <div className="w-full max-w-2xl">
            {!proof ? (
              <div className="rounded-3xl bg-white p-8 text-center text-[#8A8A8A]">Loading result…</div>
            ) : proof.available ? (
              <OrangeFixProofCard proof={proof}/>
            ) : (
              <div className="rounded-3xl bg-white p-8 text-center text-[#4A4A4A]"
                   data-testid="client-orange-fix-proof-unavailable">
                Verified proof not available.
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => { setProofFor(null); setProof(null); }}
                data-testid="client-orange-fix-proof-close"
                className="px-4 py-2 rounded-full bg-white text-[10px] font-black tracking-[0.18em] uppercase border border-[#EAEAEA] hover:border-[#FF6A00]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
